import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearReceiptsCSVReportV2,
  FETCH_RECEIPTS_CSV_REPORT_V2,
  fetchReceiptsCSVReportV2,
} from '~/actions';
import { shouldUseDsoStructure } from '~/growthbook';
import { useLoading, useParentOffice } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useUserSelector } from '~/hooks/useUserSelector';
import { ReceiptTransactionDateEnum } from '~/utils/Enum';

export const useDownloadReportV2 = () => {
  const dispatch = useDispatch();
  const user = useUserSelector();

  const isDSOStructureEnabled = useRef(shouldUseDsoStructure()).current;

  const offices = useAppSelector((state) => state.user.offices?.map((office) => office.id));
  const childOffices = useAppSelector((state) =>
    state.user.childOffices?.map((office) => office.id),
  );
  const reports = useAppSelector((state) => state.receipt.reports);

  const { isLoading } = useLoading([FETCH_RECEIPTS_CSV_REPORT_V2]);
  const { isParentOffice } = useParentOffice();

  const officeIds = useMemo(() => {
    if (!isDSOStructureEnabled && !isParentOffice) {
      return [user?.id];
    }

    if (!isDSOStructureEnabled) {
      return [user?.id, ...childOffices];
    }

    return offices;
  }, [childOffices, isDSOStructureEnabled, isParentOffice, offices, user?.id]);

  const onDownloadReport = (formDateRange: ReceiptTransactionDateEnum) => {
    dispatch(
      fetchReceiptsCSVReportV2({
        officeIds,
        transactionDate: formDateRange,
      }),
    );
  };

  useEffect(() => {
    if (typeof reports === 'string' && reports.trim() !== '') {
      const blob = new Blob([reports], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'receiptReport.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => dispatch(clearReceiptsCSVReportV2()), 350);
    }
  }, [dispatch, reports]);

  return {
    isLoading,
    reports,
    onDownloadReport,
  };
};
